import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Divider,
  Space,
  Spin,
  Alert,
  Grid,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  SearchOutlined,
  UserOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import dayjs from "dayjs";

import vendorSeal from "../assets/seal_nms.jpeg";
import vendorSign from "../assets/vendor_sign.png";

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
import api from "../services/api";

const DeclarationPage = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // =========================
  // ✅ CUSTOMER LIST
  // =========================
  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await api.get("/customers?limit=1000");

      if (response.success && Array.isArray(response.data)) {
        setCustomers(response.data);
      } else {
        message.error("Customers API returned unexpected format");
        setCustomers([]);
      }
    } catch (error) {
      message.error("Failed to fetch customers");
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (!searchCustomer) return true;
      const s = searchCustomer.toLowerCase();
      return (
        c.customerName?.toLowerCase().includes(s) ||
        c.consumerNo?.toLowerCase().includes(s) ||
        c.customerMobile?.includes(s) ||
        c.customerEmail?.toLowerCase().includes(s)
      );
    });
  }, [customers, searchCustomer]);

  // =========================
  // ✅ CUSTOMER DETAILS
  // =========================
  // Fetch customer details with orders and items
  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}?include=orders.items.item,assets.item,orders.invoices,receivedInvoices,consignedInvoices`);
      if (response && response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.error("❌ DEBUG: Network error:", error);
    }
    return null;
  };

  const pickLatestOrder = (orders = []) => {
    if (!orders.length) return null;
    const sorted = [...orders].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
    return (
      sorted.find((o) => o?.sanctionNumber && String(o.sanctionNumber).trim() !== "") ||
      sorted[0]
    );
  };

  // =========================
  // ✅ EXTRACT PV ASSETS
  // =========================
  const extractPVAssets = (customerData, order) => {
    // Try different possible paths to assets
    let assets = [];
    
    if (customerData?.assets && Array.isArray(customerData.assets)) {
      assets = customerData.assets;
    } else if (customerData?.Asset && Array.isArray(customerData.Asset)) {
      assets = customerData.Asset;
    } else if (order?.assets && Array.isArray(order.assets)) {
      assets = order.assets;
    }

    const orderId = order?.id;
    const customerId = customerData?.id;

    // Filter for Solar Panel assets
    const pvAssets = assets.filter((a) => {
      const itemType = a?.item?.itemType || a?.Item?.itemType || a?.itemType;
      const isSolarPanel = itemType === "SolarPanel";
      
      const assetOrderId = a?.orderId || a?.Order?.id || a?.order?.id;
      const matchOrder = orderId ? assetOrderId === orderId : true;
      
      const assetCustomerId = a?.customerId || a?.Customer?.id || a?.customer?.id;
      const matchCustomer = customerId ? assetCustomerId === customerId : true;
      
      return isSolarPanel && (matchOrder || matchCustomer);
    });

    const serials = pvAssets
      .map((a) => a?.serialNumber || a?.SerialNumber || a?.srNo)
      .filter(Boolean);
      
    const make = 
      pvAssets.map((a) => a?.brand || a?.Brand || a?.make).filter(Boolean)[0] ||
      pvAssets.map((a) => a?.item?.itemName || a?.Item?.itemName).filter(Boolean)[0] ||
      "";

    return { pvAssets, serials, make };
  };

  const calculatePVModuleCount = (customerData, order, serials = []) => {
    if (Array.isArray(serials) && serials.length > 0) return serials.length;

    const items = order?.items || order?.Items || [];
    if (!Array.isArray(items) || items.length === 0) return 0;

    const pvItems = items.filter((oi) => {
      const itemType = oi?.item?.itemType || oi?.Item?.itemType || oi?.itemType;
      return itemType === "SolarPanel";
    });

    const totalQty = pvItems.reduce((sum, oi) => {
      const qty = oi?.quantity ?? oi?.qty ?? 0;
      const n = typeof qty === "string" ? parseFloat(qty) : Number(qty);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);

    return totalQty > 0 ? Math.round(totalQty) : pvItems.length;
  };

  // =========================
  // ✅ PICK PURCHASE INVOICE (matched by item IDs from order)
  // =========================
  // Pick the solar panel purchase invoice by matching item name from customer's assigned assets
  const pickPurchaseInvoice = async (order, customerData) => {
    try {
      const allAssets = customerData?.assets || [];

      // Get solar panel item names from customer's assigned assets for this order
      const panelAssets = allAssets.filter(
        (a) =>
          a.item?.itemType === "SolarPanel" &&
          (a.orderId === order?.id || a.customerId === customerData?.id)
      );

      const panelItemNames = [
        ...new Set(
          panelAssets
            .map((a) => a.item?.itemName?.trim().toLowerCase())
            .filter(Boolean)
        ),
      ];

      const panelItemIds = [
        ...new Set(panelAssets.map((a) => a.itemId).filter(Boolean)),
      ];

      // Fetch purchase invoices (includes items with item details)
      const res = await api.get("/purchases?limit=200");
      const allPurchases = res?.data || [];

      if (!allPurchases.length) return null;

      let candidates = [];

      // Priority 1: invoice items whose name matches the panel assigned to this customer
      if (panelItemNames.length > 0) {
        candidates = allPurchases.filter((inv) =>
          inv.items?.some((ii) =>
            panelItemNames.includes(ii.item?.itemName?.trim().toLowerCase())
          )
        );
      }

      // Priority 2: match by item ID
      if (!candidates.length && panelItemIds.length > 0) {
        candidates = allPurchases.filter((inv) =>
          inv.items?.some((ii) => panelItemIds.includes(ii.itemId))
        );
      }

      // Priority 3: match by order item names (fallback if no assets assigned yet)
      if (!candidates.length) {
        const orderPanelNames = (order?.items || [])
          .filter((oi) => oi.item?.itemType === "SolarPanel")
          .map((oi) => oi.item?.itemName?.trim().toLowerCase())
          .filter(Boolean);

        if (orderPanelNames.length > 0) {
          candidates = allPurchases.filter((inv) =>
            inv.items?.some((ii) =>
              orderPanelNames.includes(ii.item?.itemName?.trim().toLowerCase())
            )
          );
        }
      }

      // Priority 4: any purchase invoice with any SolarPanel item
      if (!candidates.length) {
        candidates = allPurchases.filter((inv) =>
          inv.items?.some((ii) => ii.item?.itemType === "SolarPanel")
        );
      }

      // Final fallback
      if (!candidates.length) candidates = allPurchases;

      // Return most recent
      const sorted = [...candidates].sort(
        (a, b) =>
          new Date(b.invoiceDate || b.createdAt || 0) -
          new Date(a.invoiceDate || a.createdAt || 0)
      );

      return sorted[0] || null;
    } catch (err) {
      console.error("Failed to fetch purchase invoices:", err);
      return null;
    }
  };

  // =========================
  // ✅ ON ORDER CHANGE
  // =========================
  const onOrderChange = (orderId) => {
    const order = customerOrders.find((o) => o.id === orderId);
    if (!order || !selectedCustomer) return;

    setSelectedOrder(order);

    const { serials, make } = extractPVAssets(selectedCustomer, order);

    form.setFieldsValue({
      consumerNo: order?.selectedConsumerNumber || selectedCustomer?.consumerNo || "",
      capacity: order?.plantSizeKw ? `${order.plantSizeKw} KW` : "",
      // applicationNo: left blank — user enters manually
      sanctionDate: order?.registrationOnNPDate
        ? dayjs(order.registrationOnNPDate).format("DD-MM-YYYY")
        : "",

      pvModuleCount: calculatePVModuleCount(selectedCustomer, order, serials),
      pvSerialNumbers: serials.join(" / "),
      pvMake: make || form.getFieldValue("pvMake"),

      // invoiceNo, invoiceDate, ewayBillNo, cellGstInvoiceNo — user enters manually
    });

    message.info(`Switched to Order #${order.id}`);
  };

  // =========================
  // ✅ ON CUSTOMER SELECT
  // =========================
  const onCustomerSelect = async (customerId) => {
    setLoading(true);
    try {
      const customerData = await fetchCustomerDetails(customerId);

      setSelectedCustomer(customerData);

      const orders = customerData?.orders || customerData?.Orders || [];
      setCustomerOrders(orders);

      const latestOrder = pickLatestOrder(orders);
      setSelectedOrder(latestOrder);

      const addressParts = [
        customerData?.customerAddress || customerData?.address,
        customerData?.subDistrict ? `Tal: ${customerData.subDistrict}` : null,
        customerData?.district ? `District: ${customerData.district}` : null,
        "State: Maharashtra",
      ].filter(Boolean);

      const { serials, make } = extractPVAssets(customerData, latestOrder);

      form.setFieldsValue({
        customerId: customerData?.id || customerId,

        companyName: "NIYATI MULTI SERVICES",
        discomName: "Maharashtra State Electricity Distribution Co. Ltd",

        consumerName: customerData?.customerName || customerData?.name || "",
        consumerNo: latestOrder?.selectedConsumerNumber || customerData?.consumerNo || customerData?.consumerNumber || "",
        mobile: customerData?.customerMobile || customerData?.mobile || "",
        email: customerData?.customerEmail || customerData?.email || "",
        address: addressParts.join(", "),

        capacity: latestOrder?.plantSizeKw ? `${latestOrder.plantSizeKw} KW` : "",
        // applicationNo: left blank — user enters manually
        sanctionDate: latestOrder?.registrationOnNPDate
          ? dayjs(latestOrder.registrationOnNPDate).format("DD-MM-YYYY")
          : "",

        pvModuleCapacity: "540 wp",
        pvModuleCount: calculatePVModuleCount(customerData, latestOrder, serials),
        pvSerialNumbers: serials.join(" / "),
        pvMake: make || "Premier energies photovoltaic private limited",

        // invoiceNo, invoiceDate, ewayBillNo, cellGstInvoiceNo — user enters manually

        cellManufacturer: "Premier energies photovoltaic private limited",
        dcr: "",

        signName: "Vishal Hande",
        signDesignation: "Project Head",
        signPhone: "91 - 88300 89973",
        signEmail: "sales.niyatimultiservices@gmail.com",
      });

      message.success("Customer loaded successfully");
    } catch (e) {
      message.error(e.message || "Failed to fetch customer details");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // ✅ IMAGE HELPER
  // =========================
  const imageUrlToDataUrl = async (url) => {
    try {
      if (typeof url === "string" && url.startsWith("data:")) return url;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const blob = await res.blob();

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("imageUrlToDataUrl fallback for:", url, err);
      return url;
    }
  };

  // =========================
  // ✅ PDF GENERATION
  // =========================
  const generatePDF = async () => {
    try {
      const v = await form.validateFields();

      const doc = new jsPDF("p", "mm", "a4");
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const marginL = 18;
      const marginR = 18;
      const maxW = pageW - marginL - marginR;

      let y = 22;

      const ensureSpace = (need = 10) => {
        if (y + need > pageH - 18) {
          doc.addPage();
          y = 22;
        }
      };

      const setFont = (bold = false, size = 12) => {
        doc.setFont("times", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(0, 0, 0);
      };

      const center = (text, size = 12, bold = false, gap = 6) => {
        ensureSpace(10);
        setFont(bold, size);
        doc.text(text, pageW / 2, y, { align: "center" });
        y += gap;
      };

      const lineGap = 5.5;

      // Render a line with justify: distribute extra space between words
      const renderJustifiedLine = (words, startX, lineY, availW, isLastLine) => {
        if (isLastLine || words.length <= 1) {
          // Last line or single word: left-aligned with normal word spacing
          let cx = startX;
          words.forEach(({ text, bold }, i) => {
            setFont(bold, 11);
            doc.text(text, cx, lineY);
            cx += doc.getTextWidth(text);
            // Add a normal space between words (not after the last word)
            if (i < words.length - 1) {
              setFont(false, 11);
              cx += doc.getTextWidth(" ");
            }
          });
          return;
        }
        // Calculate total text width and gaps
        const totalTextW = words.reduce((sum, w) => {
          setFont(w.bold, 11);
          return sum + doc.getTextWidth(w.text);
        }, 0);
        const spaceCount = words.length - 1;
        const extraPerGap = (availW - totalTextW) / spaceCount;
        let cx = startX;
        words.forEach(({ text, bold }, i) => {
          setFont(bold, 11);
          doc.text(text, cx, lineY);
          cx += doc.getTextWidth(text) + (i < spaceCount ? extraPerGap : 0);
        });
      };

      // Render mixed bold/normal paragraph with full justification
      const addInlineParagraph = (segments, startX = marginL, availW = maxW, gapAfter = 4) => {
        // Split all segments into words with bold flag
        const words = [];
        segments.forEach(({ text, bold }) => {
          // Split on spaces but keep track of leading/trailing spaces
          const parts = text.split(" ");
          parts.forEach((part, i) => {
            if (part) words.push({ text: part, bold });
            if (i < parts.length - 1) words.push({ text: " ", bold: false, isSpace: true });
          });
        });

        // Word-wrap into lines
        const lines = [];
        let currentLine = [];
        let currentW = 0;

        words.forEach((word) => {
          if (word.isSpace) {
            // Attach trailing space to previous word for width calculation
            if (currentLine.length > 0) {
              setFont(false, 11);
              const spaceW = doc.getTextWidth(" ");
              if (currentW + spaceW < availW) {
                currentLine.push(word);
                currentW += spaceW;
              }
            }
            return;
          }
          setFont(word.bold, 11);
          const ww = doc.getTextWidth(word.text);
          if (currentW + ww > availW + 0.5 && currentLine.length > 0) {
            // Remove trailing space from line
            while (currentLine.length && currentLine[currentLine.length - 1].isSpace)
              currentLine.pop();
            lines.push([...currentLine]);
            currentLine = [];
            currentW = 0;
          }
          currentLine.push(word);
          currentW += ww;
        });
        if (currentLine.length) {
          while (currentLine.length && currentLine[currentLine.length - 1].isSpace)
            currentLine.pop();
          lines.push(currentLine);
        }

        // Filter spaces from line words for rendering (we handle spacing via justify)
        lines.forEach((line, li) => {
          ensureSpace(lineGap + 2);
          const nonSpace = line.filter((w) => !w.isSpace);
          renderJustifiedLine(nonSpace, startX, y, availW, li === lines.length - 1);
          y += lineGap;
        });
        y += gapAfter;
      };

      const addParagraph = (text, size = 11, gapAfter = 4) => {
        ensureSpace(14);
        setFont(false, size);
        const lines = doc.splitTextToSize(text, maxW);
        lines.forEach((line, i) => {
          const isLast = i === lines.length - 1;
          if (isLast) {
            doc.text(line, marginL, y);
          } else {
            doc.text(line, marginL, y, { align: "justify", maxWidth: maxW });
          }
          y += lineGap;
        });
        y += gapAfter;
      };

      const addNumbered = (num, text, size = 11) => {
        ensureSpace(14);
        setFont(false, size);
        const prefix = `${num}.  `;
        const prefixW = doc.getTextWidth(prefix);
        const lines = doc.splitTextToSize(text, maxW - prefixW);
        lines.forEach((line, i) => {
          ensureSpace(lineGap + 2);
          if (i === 0) doc.text(prefix, marginL, y);
          const isLast = i === lines.length - 1;
          if (isLast) {
            doc.text(line, marginL + prefixW, y);
          } else {
            doc.text(line, marginL + prefixW, y, { align: "justify", maxWidth: maxW - prefixW });
          }
          y += lineGap;
        });
        y += 3;
      };

      center("ANNEXURE-A", 12, true, 6);
      center(
        "Undertaking/Self- Declaration for domestic content requirement fulfillment",
        12,
        true,
        6
      );
      center("(On a plain Paper)", 11, true, 10);

      const companyName = v.companyName || "M/S NIYATI MULTI SERVICES";
      const capacity = v.capacity || "";
      const consumerName = v.consumerName || "";
      const address = v.address || "";
      const applicationNo = v.applicationNo || "";
      const sanctionDate = v.sanctionDate || "";
      const discom = v.discomName || "Maharashtra State Electricity Distribution Co. Ltd";

      addInlineParagraph([
        { text: "This is to certify that ", bold: false },
        { text: companyName, bold: true },
        { text: " [Company Name] has installed ", bold: false },
        { text: capacity, bold: true },
        { text: " Grid Connected Rooftop Solar PV Power Plant for ", bold: false },
        { text: consumerName, bold: true },
        { text: " [Consumer Name] ", bold: false },
        { text: address, bold: true },
        { text: " [Address] under application number ", bold: false },
        { text: applicationNo, bold: true },
        { text: " dated ", bold: false },
        { text: sanctionDate, bold: true },
        { text: " [Sanction date] issued by ", bold: false },
        { text: discom, bold: true },
        { text: " [DISCOM Name].", bold: false },
      ]);

      addNumbered(
        2,
        "It is hereby undertaken that the PV modules installed for the above-mentioned project are domestically manufactured using domestic manufactured solar cells. The details of installed PV Modules are follows:",
        11
      );

      // Indented block for items 1-9 (matching preview’s marginLeft: 22)
      const indent = 8; // mm indent for sub-items
      const addIndented = (text, size = 11) => {
        ensureSpace(14);
        setFont(false, size);
        const lines = doc.splitTextToSize(text, maxW - indent);
        lines.forEach((line, i) => {
          ensureSpace(lineGap + 2);
          const isLast = i === lines.length - 1;
          if (isLast || lines.length === 1) {
            doc.text(line, marginL + indent, y);
          } else {
            doc.text(line, marginL + indent, y, { align: "justify", maxWidth: maxW - indent });
          }
          y += lineGap;
        });
        y += 3;
      };

      addIndented(`1.  PV Module Capacity: ${v.pvModuleCapacity || ""}`);
      addIndented(`2.  Number of PV Modules: ${v.pvModuleCount ? `${v.pvModuleCount} Nos` : ""}`);
      addIndented(`3.  Sr. No of PV Module:`);
      addIndented(`     ${v.pvSerialNumbers || ""}`);
      addIndented(`4.  PV Module Make: ${v.pvMake || ""}`);
      addIndented(`5.  Purchase Order Number: Invoice No.- ${v.invoiceNo || ""}`);
      addIndented(`     e-Way Bill No. – ${v.ewayBillNo || ""}`);
      addIndented(`6.  Purchase Order Date: ${v.invoiceDate || ""}`);
      addIndented(`7.  Cell manufacturer’s name: ${v.cellManufacturer || ""}`);
      addIndented(`8.  Cell GST invoice No: ${v.cellGstInvoiceNo || ""}`);
      addIndented(`9.  DCR: ${v.dcr || ""}`);

      addIndented("10. The above undertaking is based on the certificate issued by PV Module manufacturer/supplier while supplying the above-mentioned order.");

      // Point 4 with inline bold for signName and companyName
      ensureSpace(14);
      const p4Prefix = "4.  ";
      setFont(false, 11);
      const p4PrefixW = doc.getTextWidth(p4Prefix);
      doc.text(p4Prefix, marginL, y);
      // Render the rest with inline bold using addInlineParagraph offset by prefix width
      // We temporarily move y back to same line, then addInlineParagraph from offset X
      const p4StartX = marginL + p4PrefixW;
      const p4AvailW = maxW - p4PrefixW;
      addInlineParagraph(
        [
          { text: "I, ", bold: false },
          { text: v.signName || "", bold: true },
          { text: " on behalf of ", bold: false },
          { text: companyName, bold: true },
          { text: " [Company Name] further declare that the information given above is true and correct and nothing has been concealed therein. " +
            "If anything is found incorrect at any stage then the due Central Financial Assistance (CFA) that I have not charged from the consumer be withheld " +
            "and appropriate action may be taken against me and my company for wrong declaration. Supporting documents and proof of the above information will be provided " +
            "as and when requested by MNRE.", bold: false },
        ],
        p4StartX,
        p4AvailW,
        3
      );

      ensureSpace(35);
      setFont(false, 11);
      doc.text("(Signature With official Seal)", marginL, y);
      y += 8;

      const signW = 32;
      const signH = 12;
      const sealW = 18;
      const sealH = 18;
      const gap = 8;

      const leftBlockX = marginL;
      const leftBlockY = y;

      try {
        const signData = await imageUrlToDataUrl(vendorSign);
        doc.addImage(signData, "PNG", leftBlockX, leftBlockY, signW, signH);
      } catch (e) {
        console.warn("Signature image failed:", e);
      }

      try {
        const sealData = await imageUrlToDataUrl(vendorSeal);
        doc.addImage(sealData, "JPEG", leftBlockX + signW + gap, leftBlockY - 3, sealW, sealH);
      } catch (e) {
        console.warn("Seal image failed:", e);
      }

      y = leftBlockY + Math.max(signH, sealH) + 6;

      const rightX = marginL + 95;
      const topY = leftBlockY + 2;

      // "For <companyName>" — "For " normal, company name bold
      setFont(false, 11);
      const forLabel = "For ";
      doc.text(forLabel, rightX, topY);
      setFont(true, 11);
      doc.text(companyName, rightX + doc.getTextWidth(forLabel), topY);

      // "Name: <signName>" — "Name: " normal, name bold
      setFont(false, 11);
      const nameLabel = "Name: ";
      doc.text(nameLabel, rightX, topY + 6);
      setFont(true, 11);
      doc.text(v.signName || "", rightX + doc.getTextWidth(nameLabel), topY + 6);

      setFont(false, 11);
      doc.text(
        `Designation: ${v.signDesignation || ""}    Phone:${v.signPhone || ""}`,
        rightX,
        topY + 12
      );
      doc.text(`Email: ${v.signEmail || ""}`, rightX, topY + 18);

      const filename = `Annexure-A_${(consumerName || "Customer")
        .replace(/\s+/g, "_")}_${dayjs().format("DDMMYYYY")}.pdf`;

      doc.save(filename);
      message.success("PDF generated successfully");
    } catch (err) {
      console.error("PDF generation error:", err);
      message.error("PDF generation failed / fill required fields");
    }
  };

  return (
    <div style={{ padding: isMobile ? '8px' : '24px' }}>
      <Card
        headStyle={{ padding: isMobile ? '0 12px' : '0 24px' }}
        bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined style={{ color: "#fa8c16" }} />
            <span style={{ fontSize: isMobile ? '13px' : undefined }}>
              {isMobile ? 'Annexure-A' : 'Declaration / Undertaking (Annexure-A)'}
            </span>
          </div>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchCustomers}
            loading={loadingCustomers}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : 'Refresh Customers'}
          </Button>
        }
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            <Divider>
              <Space>
                <UserOutlined />
                <span>Step 1: Select Customer</span>
              </Space>
            </Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Search and Select Customer"
                  name="customerId"
                  rules={[{ required: true, message: "Please select a customer" }]}
                >
                  <Select
                    showSearch
                    size="large"
                    placeholder={
                      <span>
                        <SearchOutlined style={{ marginRight: 8 }} />
                        Click to load customers / type to search...
                      </span>
                    }
                    filterOption={false}
                    onDropdownVisibleChange={(open) => {
                      if (open && customers.length === 0) fetchCustomers();
                    }}
                    onSearch={setSearchCustomer}
                    onChange={onCustomerSelect}
                    loading={loadingCustomers}
                    notFoundContent={
                      loadingCustomers ? (
                        <div className="py-3 text-center">
                          <Spin size="small" /> Loading customers...
                        </div>
                      ) : (
                        <div className="py-3 text-center">No customers found</div>
                      )
                    }
                  >
                    {filteredCustomers.map((c) => (
                      <Option key={c.id} value={c.id}>
                        <div className="flex flex-col py-1">
                          <div className="flex items-center gap-2">
                            <UserOutlined className="text-blue-500" />
                            <span className="font-medium">{c.customerName}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {c.consumerNo && <span>Consumer: {c.consumerNo}</span>}
                            {c.customerMobile && <span> | {c.customerMobile}</span>}
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {customerOrders.length > 0 && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Order (auto-selected latest)" name="orderPick">
                    <Select
                      value={selectedOrder?.id}
                      onChange={onOrderChange}
                      style={{ width: "100%" }}
                    >
                      {customerOrders.map((o) => (
                        <Option key={o.id} value={o.id}>
                          Order #{o.id} — {o.plantSizeKw || 0}KW —{" "}
                          {o.projectStatus || "Open"}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
              <Col xs={24} lg={10}>
                <Divider />
                <Title level={5}>Auto-populated Fields (editable)</Title>

                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Company Name"
                      name="companyName"
                      rules={[{ required: true, message: "Company name required" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="DISCOM Name" name="discomName">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Consumer Name"
                      name="consumerName"
                      rules={[{ required: true, message: "Consumer name required" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Consumer No" name="consumerNo">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Plant Capacity" name="capacity">
                      <Input placeholder="e.g. 3KW" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Address"
                  name="address"
                  rules={[{ required: true, message: "Address required" }]}
                >
                  <Input />
                </Form.Item>

                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Application / Ref No" name="applicationNo">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Sanction Date" name="sanctionDate">
                      <Input placeholder="DD-MM-YYYY" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />
                <Title level={5}>PV Modules (auto from assets)</Title>

                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="PV Module Capacity" name="pvModuleCapacity">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="No. of PV Modules" name="pvModuleCount">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="PV Make / Manufacturer" name="pvMake">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="PV Serial Numbers" name="pvSerialNumbers">
                  <TextArea rows={3} placeholder="Serial1 / Serial2 / ..." />
                </Form.Item>

                <Divider />
                <Title level={5}>Invoice / Dispatch (enter manually)</Title>

                <Row gutter={[12, 0]}>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Invoice No" name="invoiceNo">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Invoice Date" name="invoiceDate">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="E-Way Bill No" name="ewayBillNo">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Cell GST Invoice No" name="cellGstInvoiceNo">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Cell Manufacturer" name="cellManufacturer">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="DCR" name="dcr">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />
                <Title level={5}>Signatory</Title>

                <Row gutter={[12, 0]}>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Name" name="signName" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Designation" name="signDesignation">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Mobile" name="signPhone">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label="Email" name="signEmail">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>


                <Divider />

                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={generatePDF}
                    size="large"
                    block={isMobile}
                  >
                    Generate PDF
                  </Button>
                </div>
              </Col>

              <Col xs={24} lg={14}>
                <Card
                  title={
                    <Space>
                      <FileTextOutlined style={{ color: "#52c41a" }} />
                      Annexure-A Live Preview
                    </Space>
                  }
                  bordered={false}
                  style={{
                    borderRadius: 16,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    position: isMobile ? "static" : "sticky",
                    top: 24,
                  }}
                >
                  <div
                    style={{
                      background: "#f0f2f5",
                      padding: 15,
                      borderRadius: 12,
                      overflowY: "auto",
                      maxHeight: "100vh",
                    }}
                  >
                    <div
                      style={{
                        padding: 30,
                        fontFamily: "Times New Roman, serif",
                        fontSize: "11pt",
                        color: "#000",
                        backgroundColor: "#fff",
                        lineHeight: "1.4",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        width: "100%",
                        minHeight: "297mm",
                      }}
                    >
                      <div style={{ textAlign: "center", marginBottom: 12 }}>
                        <div style={{ fontWeight: "bold" }}>ANNEXURE-A</div>
                        <div style={{ fontWeight: "bold" }}>
                          Undertaking/Self- Declaration for domestic content requirement fulfillment
                        </div>
                        <div style={{ fontWeight: "bold" }}>(On a plain Paper)</div>
                      </div>

                      <p style={{ marginTop: 10 }}>
                        This is to certify that{" "}
                        <b>{formValues?.companyName || ""}</b> [Company Name] has installed{" "}
                        <b>{formValues?.capacity || ""}</b> Grid Connected Rooftop Solar PV Power
                        Plant for <b>{formValues?.consumerName || ""}</b> [Consumer Name]{" "}
                        <b>{formValues?.address || ""}</b> [Address] under application number{" "}
                        <b>{formValues?.applicationNo || ""}</b> dated{" "}
                        <b>{formValues?.sanctionDate || ""}</b> [Sanction date] issued by{" "}
                        <b>{formValues?.discomName || ""}</b> [DISCOM Name].
                      </p>

                      <p style={{ marginTop: 10 }}>
                        2.&nbsp;&nbsp;It is hereby undertaken that the PV modules installed for the
                        above-mentioned project are domestically manufactured using domestic
                        manufactured solar cells. The details of installed PV Modules are follows:
                      </p>

                      <div style={{ marginLeft: 22 }}>
                        <p style={{ margin: "4px 0" }}>
                          1.&nbsp;&nbsp;PV Module Capacity: {formValues?.pvModuleCapacity || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          2.&nbsp;&nbsp;Number of PV Modules: {formValues?.pvModuleCount || ""} Nos
                        </p>
                        <p style={{ margin: "4px 0" }}>3.&nbsp;&nbsp;Sr. No of PV Module:</p>
                        <p style={{ margin: "4px 0", paddingLeft: "24px" }}>{formValues?.pvSerialNumbers || ""}</p>
                        <p style={{ margin: "4px 0" }}>
                          4.&nbsp;&nbsp;PV Module Make: {formValues?.pvMake || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          5.&nbsp;&nbsp;Purchase Order Number: Invoice No.- {formValues?.invoiceNo || ""}
                        </p>
                        <p style={{ margin: "4px 0", paddingLeft: "24px" }}>
                          e-Way Bill No. – {formValues?.ewayBillNo || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          6.&nbsp;&nbsp;Purchase Order Date: {formValues?.invoiceDate || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          7.&nbsp;&nbsp;Cell manufacturer’s name: {formValues?.cellManufacturer || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          8.&nbsp;&nbsp;Cell GST invoice No: {formValues?.cellGstInvoiceNo || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          9.&nbsp;&nbsp;DCR: {formValues?.dcr || ""}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          10.&nbsp;&nbsp;The above undertaking is based on the certificate issued by PV
                          Module manufacturer/supplier while supplying the above-mentioned order.
                        </p>
                      </div>

                      <p style={{ marginTop: 10 }}>
                        4.&nbsp;&nbsp;I, <b>{formValues?.signName || ""}</b> on behalf of{" "}
                        <b>{formValues?.companyName || ""}</b> [Company Name] further declare that
                        the information given above is true and correct and nothing has been
                        concealed therein. If anything is found incorrect at any stage then the due
                        Central Financial Assistance (CFA) that I have not charged from the consumer
                        be withheld and appropriate action may be taken against me and my company for
                        wrong declaration. Supporting documents and proof of the above information
                        will be provided as and when requested by MNRE.
                      </p>

                      <p style={{ marginTop: 22 }}>(Signature With official Seal)</p>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: 10,
                          alignItems: "flex-end",
                        }}
                      >
                        <div style={{ textAlign: "left" }}>
                          <img
                            src={vendorSign}
                            alt="Vendor Sign"
                            style={{ height: 45, width: "auto" }}
                          />
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <img
                            src={vendorSeal}
                            alt="Vendor Seal"
                            style={{ height: 70, width: "auto" }}
                          />
                        </div>
                      </div>

                      <div style={{ textAlign: "right", marginTop: 14 }}>
                        <div>For {formValues?.companyName || ""}</div>
                      </div>

                      <div style={{ marginTop: 14, marginLeft: 80 }}>
                        <div>Name: {formValues?.signName || ""}</div>
                        <div style={{ marginTop: 6 }}>
                          Designation: {formValues?.signDesignation || ""} &nbsp;&nbsp; Phone:
                          {formValues?.signPhone || ""}
                        </div>
                        <div style={{ marginTop: 6 }}>Email: {formValues?.signEmail || ""}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default DeclarationPage;
